import { collection, addDoc, getDocs, query, where, orderBy, updateDoc, doc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

const LEADS_COLLECTION = 'leads';

// Phone number formatting utility
const formatPhoneForStorage = (phone) => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format as: 0744 155 921
  if (cleaned.length >= 1) {
    let formatted = '';
    
    // Add first digit (0)
    if (cleaned.length >= 1) {
      formatted += cleaned.substring(0, 1);
    }
    
    // Add first group (3 digits after 0)
    if (cleaned.length >= 4) {
      formatted += cleaned.substring(1, 4);
    } else if (cleaned.length > 1) {
      formatted += cleaned.substring(1);
      return formatted;
    }
    
    // Add space and second group (3 digits)
    if (cleaned.length >= 7) {
      formatted += ' ' + cleaned.substring(4, 7);
    } else if (cleaned.length > 4) {
      formatted += ' ' + cleaned.substring(4);
      return formatted;
    }
    
    // Add space and third group (3 digits)
    if (cleaned.length >= 10) {
      formatted += ' ' + cleaned.substring(7, 10);
    } else if (cleaned.length > 7) {
      formatted += ' ' + cleaned.substring(7);
      return formatted;
    }
    
    // Add remaining digits if any
    if (cleaned.length > 10) {
      formatted += ' ' + cleaned.substring(10);
    }
    
    return formatted;
  }
  
  return phone;
};

export const leadService = {
  // Add a new lead
  async addLead(leadData) {
    try {
      console.log('Attempting to add lead:', leadData);
      console.log('Firebase DB instance:', db);
      
      // Format phone number before saving
      const formattedPhone = formatPhoneForStorage(leadData.phone);
      
      const docRef = await addDoc(collection(db, LEADS_COLLECTION), {
        ...leadData,
        phone: formattedPhone,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log('Lead added successfully with ID:', docRef.id);
      return { id: docRef.id, ...leadData, phone: formattedPhone };
    } catch (error) {
      console.error('Error adding lead:', error);
      console.error('Error details:', error.message);
      console.error('Error code:', error.code);
      throw error;
    }
  },

  // Get all leads
  async getAllLeads() {
    try {
      console.log('Fetching all leads...');
      const q = query(
        collection(db, LEADS_COLLECTION),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const leads = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Fetched leads:', leads);
      return leads;
    } catch (error) {
      console.error('Error getting leads:', error);
      console.error('Error details:', error.message);
      throw error;
    }
  },

  // Search lead by phone number
  async searchByPhone(phoneNumber) {
    try {
      console.log('Searching for phone:', phoneNumber);
      
      // Format the search phone number
      const formattedPhone = formatPhoneForStorage(phoneNumber);
      
      const q = query(
        collection(db, LEADS_COLLECTION),
        where('phone', '==', formattedPhone)
      );
      const querySnapshot = await getDocs(q);
      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Search results:', results);
      return results;
    } catch (error) {
      console.error('Error searching by phone:', error);
      throw error;
    }
  },

  // Update lead
  async updateLead(leadId, updateData) {
    try {
      console.log('Updating lead:', leadId, updateData);
      
      // Format phone number if it's being updated
      if (updateData.phone) {
        updateData.phone = formatPhoneForStorage(updateData.phone);
      }
      
      const leadRef = doc(db, LEADS_COLLECTION, leadId);
      await updateDoc(leadRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
      
      console.log('Lead updated successfully');
      return { id: leadId, ...updateData };
    } catch (error) {
      console.error('Error updating lead:', error);
      throw error;
    }
  },

  // Delete lead
  async deleteLead(leadId) {
    try {
      console.log('Deleting lead:', leadId);
      await deleteDoc(doc(db, LEADS_COLLECTION, leadId));
      console.log('Lead deleted successfully');
      return leadId;
    } catch (error) {
      console.error('Error deleting lead:', error);
      throw error;
    }
  },

  // Search leads by multiple criteria
  async searchLeads(searchTerm) {
    try {
      const allLeads = await this.getAllLeads();
      return allLeads.filter(lead =>
        lead.phone?.includes(searchTerm) ||
        lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.company?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } catch (error) {
      console.error('Error searching leads:', error);
      throw error;
    }
  }
};